import type { ModelAnswer, QuestionRecord, Subject } from './types'

export const PAPERS = [
  {id:'economics-1',subject:'Economics',number:1,title:'Microeconomics and Business Economics',label:'Paper 1 · Microeconomics & business economics'},
  {id:'economics-2',subject:'Economics',number:2,title:'Macroeconomics and the Global Economy',label:'Paper 2 · Macroeconomics & the global economy'},
  {id:'business-1',subject:'Business',number:1,title:'Investigating small businesses',label:'Paper 1 · Investigating small businesses'},
  {id:'business-2',subject:'Business',number:2,title:'Investigating large businesses',label:'Paper 2 · Investigating large businesses'},
] as const

export type PaperId = typeof PAPERS[number]['id']
type AnswerScope = Pick<ModelAnswer,'subject'|'topicId'>
type PaperQuestion = Pick<QuestionRecord,'subject'|'paper'> & {validated?:boolean}

export function questionPaperId(question: Pick<PaperQuestion,'subject'|'paper'>): PaperId | undefined {
  if(typeof question.paper!=='string') return undefined
  const code = question.paper.match(/\b(4BS1|4EC1)\s*\/\s*0?([12])R?\b/i)
  if(code && code[1].toUpperCase() !== (question.subject==='Business'?'4BS1':'4EC1')) return undefined
  const number = code?.[2] ?? question.paper.match(/\bPaper\s+0?([12])R?\b/i)?.[1]
  return PAPERS.find(p=>p.subject===question.subject && String(p.number)===number)?.id
}

// Economics has separate syllabus content. Business tests the whole syllabus
// in both papers; its filter identifies observed, validated question links only.
export function answerPaperIds(answer:AnswerScope, questions:PaperQuestion[]): PaperId[] {
  if(answer.subject==='Economics') {
    if(answer.topicId.startsWith('eco-1-')) return ['economics-1']
    if(answer.topicId.startsWith('eco-2-')) return ['economics-2']
    return []
  }
  const seen=new Set(questions.filter(q=>q.subject===answer.subject&&q.validated).map(questionPaperId))
  return PAPERS.filter(p=>p.subject===answer.subject&&seen.has(p.id)).map(p=>p.id)
}

export function answerPaperLabel(answer:AnswerScope, ids:PaperId[]):string {
  if(answer.subject==='Business') return ids.length ? `Both papers · linked questions: ${ids.map(id=>`Paper ${PAPERS.find(p=>p.id===id)!.number}`).join(' & ')}` : 'Both papers · shared syllabus'
  return ids.map(id=>PAPERS.find(p=>p.id===id)?.label).filter(Boolean).join(' · ')
}

export function paperOptions(subject:Subject|'All'|string) {
  return PAPERS.filter(p=>subject==='All'||p.subject===subject)
}

export function paperFilterLabel(id:string) {
  const paper=PAPERS.find(p=>p.id===id)
  return paper ? `${paper.subject} · ${paper.label}` : id
}

export function linkedQuestions(answer:Pick<ModelAnswer,'id'|'questionIds'>, library:{questions:QuestionRecord[];recentAssociations?:{questionId:string;answerIds:string[]}[]}):QuestionRecord[] {
  const ids=new Set([...answer.questionIds,...(library.recentAssociations??[]).filter(link=>link.answerIds.includes(answer.id)).map(link=>link.questionId)])
  return library.questions.filter(q=>ids.has(q.id))
}
